/**
 * @file FontTypeFields.tsx
 * @description Начертание, регистр, размер и разрядка поля «Шрифт» элемента
 * (phase19.16, партия SB120 «свобода выбора», этап 0; site-builder.md SB120
 * «размер текста — числом в разумных пределах»; uiux-sb120-fields §4):
 *   - Начертание — «Как у роли» и только веса, которые есть у шрифта
 *     (как `HeadingTypeFields`); на 360 — числа, от sm — «Обычное 400»;
 *   - Регистр — «Как у роли / Как написано / ПРОПИСНЫЕ»;
 *   - Размер — `NumberSliderField` 10–96 px, наследуемое «↺ 32 px (H2)»,
 *     ступени уровней [24 H3][32 H2][48 H1]; мелкий H1 — подсказка, не запрет;
 *     дроби без шага (SB120 этап 3а: подъём v3 → v4 даёт 17,9375 px, ввод —
 *     до двух знаков); под ползунком — `sizeExtra` (строка «на телефоне N px
 *     · Своё ›», uiux-sb120-stage3 §2.1);
 *   - Разрядка — `NumberSliderField` 0–0,4 em шагом 0,01.
 * Нет значения — «как у роли» (`undefined`).
 */

import { ELEMENT_FONT_LIMITS, nearestFontWeight, type ElementFontCase, type ElementFontStyle, type SiteFontRegistry } from "@chaiberi/shared";
import type { ReactNode } from "react";

import { Label } from "../../ui/label";
import { FieldHint } from "../../ui/settings-groups";
import { Segmented, type SegmentedOption } from "../../site-view/Segmented";
import { NumberSliderField } from "../NumberSliderField";
import type { NumberPreset } from "../NumberPresets";
import { weightChoices, type FontFieldPatch } from "../../../lib/site-fields/font-field-model";
import { formatWithUnit } from "../../../lib/site-fields/number-input";

const INHERIT = "inherit";
/** Размер — px с дробями до сотых: 17,94 не округляется до 18 (своё значение хранится как есть). */
const SIZE_DISPLAY = { factor: 1, decimals: 2 } as const;

const CASE_OPTIONS: readonly SegmentedOption<ElementFontCase | typeof INHERIT>[] = [
  { value: INHERIT, label: "Как у роли" },
  { value: "asis", label: "Как написано" },
  { value: "upper", label: "ПРОПИСНЫЕ" },
];

/** Ступени разрядки — те же, что «обычная / широкая / очень» надписей темы. */
const TRACKING_PRESETS: readonly NumberPreset[] = [
  { label: "0 обычная", value: 0 },
  { label: "0,08 широкая", value: 0.08 },
  { label: "0,2 очень", value: 0.2 },
];

export interface FontTypeInherit {
  /** Размер «как у роли/уровня», px. */
  sizePx: number;
  /** Чей размер: «H2». */
  label: string;
  /** Разрядка «как у роли», em; нет — 0. */
  trackingEm?: number;
  /** Подпись вместо «32 px (H2)», если одного числа нет: «по ширине плитки». */
  sizeText?: string;
}

interface Props extends ElementFontStyle {
  /** Шрифт, который стоит у элемента (для списка начертаний). */
  fontKey: string;
  registry: SiteFontRegistry;
  inherit: FontTypeInherit;
  sizePresets?: readonly NumberPreset[];
  /** Мягкая подсказка о мелком размере (H1 < 24 px). */
  sizeHint?: { below: number; text: string };
  /** Под ползунком размера: строка телефона (`DeviceValueRow`). */
  sizeExtra?: ReactNode;
  disabled?: boolean;
  onChange: (patch: FontFieldPatch) => void;
  onPreview?: (patch: FontFieldPatch) => void;
  onHover?: (inside: boolean) => void;
}

export function FontTypeFields(props: Props) {
  const { fontKey, registry, weight, case: textCase, size, tracking, inherit, sizePresets, sizeHint, disabled, onChange, onPreview, onHover } = props;
  const weightOptions: SegmentedOption<string>[] = [
    { value: INHERIT, label: "Как у роли", disabled },
    ...weightChoices(fontKey, registry).map((w) => ({
      value: String(w.value),
      ariaLabel: w.full,
      label: (
        <>
          <span className="max-sm:hidden">{w.full}</span>
          <span className="sm:hidden">{w.short}</span>
        </>
      ),
      disabled,
    })),
  ];
  // Сохранённый вес, которого у нового шрифта нет, показываем ближайшим (как на сайте).
  const weightValue = weight === undefined ? INHERIT : String(nearestFontWeight(fontKey, weight, registry));
  const L = ELEMENT_FONT_LIMITS;
  const trackingInherit = inherit.trackingEm ?? 0;
  const smallSize = sizeHint !== undefined && (size ?? inherit.sizePx) < sizeHint.below;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex min-w-0 flex-col gap-1">
        <Label>Начертание</Label>
        <Segmented
          aria-label="Начертание"
          options={weightOptions}
          value={weightValue}
          onChange={(next) => onChange({ weight: next === INHERIT ? undefined : Number(next) })}
        />
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <Label>Регистр</Label>
        <Segmented
          aria-label="Регистр"
          options={disabled ? CASE_OPTIONS.map((o) => ({ ...o, disabled })) : CASE_OPTIONS}
          value={textCase ?? INHERIT}
          onChange={(next) => onChange({ case: next === INHERIT ? undefined : next })}
        />
      </div>
      <div className="flex min-w-0 flex-col">
        <NumberSliderField
          label="Размер"
          value={size}
          inherit={{ value: inherit.sizePx, label: inherit.sizeText ?? `${formatWithUnit(inherit.sizePx, 2, "px")} (${inherit.label})` }}
          min={L.size.min}
          max={L.size.max}
          step={1}
          largeStep={8}
          display={SIZE_DISPLAY}
          unit="px"
          presets={sizePresets}
          disabled={disabled}
          onChange={(next) => onChange({ size: next })}
          onPreview={onPreview ? (next) => onPreview({ size: next }) : undefined}
          onHover={onHover}
        />
        {sizeHint && smallSize ? <FieldHint>{sizeHint.text}</FieldHint> : null}
        {props.sizeExtra}
      </div>
      <NumberSliderField
        label="Разрядка"
        value={tracking}
        inherit={{ value: trackingInherit, label: formatWithUnit(trackingInherit, 2, "em") }}
        min={L.tracking.min}
        max={L.tracking.max}
        step={L.tracking.step}
        largeStep={0.1}
        unit="em"
        display={{ factor: 1, decimals: 2 }}
        presets={TRACKING_PRESETS}
        disabled={disabled}
        onChange={(next) => onChange({ tracking: next })}
        onPreview={onPreview ? (next) => onPreview({ tracking: next }) : undefined}
        onHover={onHover}
      />
    </div>
  );
}
